import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { DestroyRef, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import {
  EntityId,
  addEntities,
  addEntity,
  removeAllEntities,
  removeEntities,
  removeEntity,
  updateEntity,
  upsertEntities,
  upsertEntity,
  withEntities
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { get } from 'lodash/fp';
import Computer, { Agent, AgentState, AgentType } from 'mbs-ui-shared@models/Computer';
import SummaryStat from 'mbs-ui-shared@models/rmm/SummaryStat';
import * as AppsActions from 'mbs-ui-shared@modules/computer-apps/store/computer-apps.actions';
import { RmmService } from 'mbs-ui-shared@services/rmm.service';
import { isDataObsolete } from 'mbs-ui-shared@utils/store';
import moment from 'moment';
import { EMPTY, pipe } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { SummaryComputer } from './store/summary-computer.model';

const selectIdConfig = { selectId: (e: SummaryComputer) => e.id };
const obsolescenceTimeout = 10; // minutes

export const SummaryComputersStore = signalStore(
  { providedIn: 'root' },
  withDevtools('SummaryComputersStore'),

  withEntities<SummaryComputer>(),

  withState({
    current: null as Computer | null,
    loading: false,
    access: true
  }),

  // -------------------------------------------------------------------------
  // Computed signals (replaces selectors)
  // -------------------------------------------------------------------------
  withComputed((store) => ({
    selectCurrentHid: computed(() => store.current()?.hid),
    selectCurrentComputer: computed(() => store.current()),
    selectCurrent: computed(() => store.entityMap()[store.current()?.hid]),
    selectLoading: computed(() => store.loading()),
    selectAccessState: computed(() => store.access()),
    selectCurrentSystemTime: computed(() => store.entityMap()[store.current()?.hid]?.systemTime)
  })),

  // -------------------------------------------------------------------------
  // Methods — async load, CRUD, and selector factories
  // -------------------------------------------------------------------------
  withMethods((store) => {
    const rmmService = inject(RmmService);

    return {
      selectByHid: (hid: string) => computed(() => store.entityMap()[hid]),
      selectLoadingByHid: (hid: string) => computed(() => (store.entityMap()[hid] ? store.entityMap()[hid].loading : true)),
      selectDiskByHid: (hid: string) => computed(() => store.entityMap()[hid]?.hdd),
      selectCpuByHid: (hid: string) => computed(() => store.entityMap()[hid]?.cpu),
      selectMemoryByHid: (hid: string) => computed(() => store.entityMap()[hid]?.ram),
      selectAntivirusByHid: (hid: string) => computed(() => store.entityMap()[hid]?.antivirus),
      selectEventLogByHid: (hid: string) => computed(() => store.entityMap()[hid]?.eventLog),
      selectUpdateByHid: (hid: string) => computed(() => store.entityMap()[hid]?.update),
      selectAlertsByHid: (hid: string) => computed(() => store.entityMap()[hid]?.alerts),
      selectSmartByHid: (hid: string) => computed(() => store.entityMap()[hid]?.smart),
      selectTemperatureByHid: (hid: string) => computed(() => store.entityMap()[hid]?.temperature),
      selectComputerStateByHid: (hid: string) => computed(() => store.entityMap()[hid]?.computerState),
      selectSystemTimeByHid: (hid: string) => computed(() => store.entityMap()[hid]?.systemTime),
      selectAppTimeDiffByHid: (hid: string, unit: 'milliseconds' | 'seconds' | 'minutes' | 'hours' = 'hours') =>
        computed(() => {
          const time = (store.entityMap()[hid]?.systemTime as any) ?? {};
          const systemTime = time.value ? new Date(time.value) : new Date();
          return moment(systemTime).diff(moment(), unit);
        }),

      // Async load with 10-min cache (replaces loadOne$ effect)
      loadSummaryComputer: rxMethod<{ hid: string; force?: boolean; optional?: any }>(
        pipe(
          mergeMap(({ hid, force, optional }) => {
            let app: Agent;

            if (optional) {
              const apps: Agent[] = get(['apps'], optional) || [];
              app = apps.find((a) => a.applicationId === AgentType.RMM);
            }

            patchState(store, upsertEntity({ id: hid, loading: true } as SummaryComputer, selectIdConfig), { loading: true, access: true });

            const existing = store.entityMap()[hid];
            if (existing && !force && existing.timestamp && !isDataObsolete(existing.timestamp, obsolescenceTimeout)) {
              patchState(store, upsertEntity({ ...existing, loading: false } as SummaryComputer, selectIdConfig), { loading: false });
              return EMPTY;
            }

            return rmmService.fetchLastData<SummaryStat>('summary', hid).pipe(
              tapResponse({
                next: (data: any) => {
                  const summary: SummaryStat = get(['data', 0], data) || {};
                  const summaryComputer: SummaryComputer = {
                    id: hid,
                    timestamp: moment().valueOf(),
                    loading: false,
                    online: data.isOnline,
                    ...(app ? { monitoring: app.applicationState === AgentState.Enabled } : {}),
                    ...summary
                  };
                  patchState(store, upsertEntity(summaryComputer, selectIdConfig), { loading: false });
                },
                error: (error: any) => {
                  patchState(store, { loading: false, access: error?.status !== 401 });
                }
              })
            );
          })
        )
      ),

      // CRUD (replaces corresponding actions)
      upsertSummaryComputer(summaryComputer: SummaryComputer): void {
        patchState(store, upsertEntity(summaryComputer, selectIdConfig));
      },

      upsertSummaryComputers(summaryComputers: SummaryComputer[]): void {
        patchState(store, upsertEntities(summaryComputers, selectIdConfig));
      },

      addSummaryComputer(summaryComputer: SummaryComputer): void {
        patchState(store, addEntity(summaryComputer, selectIdConfig));
      },

      addSummaryComputers(summaryComputers: SummaryComputer[]): void {
        patchState(store, addEntities(summaryComputers, selectIdConfig));
      },

      updateSummaryComputer(update: { id: EntityId; changes: Partial<SummaryComputer> }): void {
        patchState(store, updateEntity(update));
      },

      updateSummaryComputers(updates: { id: EntityId; changes: Partial<SummaryComputer> }[]): void {
        updates.forEach((update) => patchState(store, updateEntity(update)));
      },

      deleteSummaryComputer(id: string): void {
        patchState(store, removeEntity(id));
      },

      deleteSummaryComputers(ids: string[]): void {
        patchState(store, removeEntities(ids));
      },

      clearSummaryComputers(): void {
        patchState(store, removeAllEntities());
      }
    };
  }),

  // Second block: setCurrentComputer depends on loadSummaryComputer from block 1
  withMethods((store) => ({
    // Replaces setCurrentComputer action + onSetCurrentComputer$ effect
    setCurrentComputer(current: Computer): void {
      if (current === store.current()) return;
      patchState(store, { current });
      if (!current?.hid || store.entityMap()[current.hid]) return;
      store.loadSummaryComputer({ hid: current.hid });
    }
  })),

  // Replaces monitoring$ effect: syncs monitoring state when RMM agent state changes
  withHooks((store) => ({
    onInit() {
      const destroyRef = inject(DestroyRef);
      inject(Actions)
        .pipe(
          ofType(AppsActions.changeMonitoringState),
          filter(({ appType }) => appType === AgentType.RMM),
          takeUntilDestroyed(destroyRef)
        )
        .subscribe(({ hid, monitoring }) => {
          patchState(store, upsertEntity({ id: hid, monitoring: monitoring === AgentState.Enabled } as SummaryComputer, selectIdConfig));
        });
    }
  }))
);

export type SummaryComputersStore = InstanceType<typeof SummaryComputersStore>;
